/// <amd-module name="DistributedTaskUI/ServiceEndpoints/Details/Components/ServiceEndpointComponent" />
import * as React from "react";

import { ActionCreatorManager } from "DistributedTask/Flux/ActionCreatorManager";
import { StoreManager } from "DistributedTask/Flux/StoreManager";
import { ServiceEndpointActionsCreator } from "DistributedTaskUI/ServiceEndpoints/Details/Actions/ServiceEndpointActionsCreator";
import { ServiceEndpointUrlsComponent } from "DistributedTaskUI/ServiceEndpoints/Details/Components/ServiceEndpointUrlsComponent";
import * as Resources from "DistributedTaskUI/ServiceEndpoints/Details/Resources";
import { ConversionStatus, IServiceEndpointComponentState, ServiceEndpointStore } from "DistributedTaskUI/ServiceEndpoints/Details/Stores/ServiceEndpointStore";
import { AzureSpnOperationStatus, ServiceEndpointAuthenticationScheme } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";
import * as EndpointConstants from "ServiceEndpoints/Client/Constants/ServiceEndpoint";

import { Card } from "VSSUI/Card";
import { Dialog } from "VSSUI/Dialog";
import { TitleSize } from "VSSUI/Header";
import { Icon } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { VssPersona } from "VSSUI/VssPersona";

import * as StringUtil from "VSS/Core/Util/String";
import { isFeatureEnabled, isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { ServiceEndpointFinalizeDraftBannerComponent } from "./ServiceEndpointFinalizeDraftBannerComponent";
import { ServiceEndpointFinalizeDraftDetailsComponent } from "./ServiceEndpointFinalizeDraftDetailsComponent";
import { ServiceEndpointUpgradeBannerComponent } from "./ServiceEndpointUpgradeBannerComponent";
import { ServiceEndpointUpgradeDetailsComponent } from "./ServiceEndpointUpgradeDetailsComponent";
import { ServiceEntpointFeatureFlags } from "Constants";

export interface IServiceEndpointComponentProps {
    onFinalizeClick: () => void;
}

export interface IServiceEndpointDetailsComponentState extends IServiceEndpointComponentState {
    isRevertConfirmationDialogueOpen?: boolean;
}

export class ServiceEndpointComponent extends VssComponent<IServiceEndpointComponentProps, IServiceEndpointDetailsComponentState>{
    private readonly _workloadIdentityFederationEnabled: boolean = false;
    private readonly _allowCreatingEndpointsInDraftStateEnabled: boolean = false;

    constructor(props: IServiceEndpointComponentProps, context: IVssComponentContext) {
        super(props, context);
        this._actionCreator = ActionCreatorManager.GetActionCreator<ServiceEndpointActionsCreator>(ServiceEndpointActionsCreator, "service-endpoints-section-actionscreator");
        this._store = StoreManager.GetStore<ServiceEndpointStore>(ServiceEndpointStore, ServiceEndpointStore.getKey());
        this.state = {
            ...ServiceEndpointStore.getDefaultState(),
            isRevertConfirmationDialogueOpen: false
        };

        this._workloadIdentityFederationEnabled = isFeatureEnabled(context.pageContext, "ms.vss-distributedtask-web.workload-identity-federation", false);
        this._allowCreatingEndpointsInDraftStateEnabled = isFeatureFlagEnabled(context.pageContext, "ServiceEndpoints.AllowCreatingEndpointsInDraftState", false);
    }

    public UNSAFE_componentWillMount(): void {
        this.setState(this._store.getState());
    }

    public componentDidMount(): void {
        if (!this.state.monitorUpgradeProcess && this.state.conversionStatus === ConversionStatus.IN_PROGRESS) {
            setTimeout(() => this._actionCreator.monitorSchemeConversionProgress(this.state.serviceEndpoint.id));
        }
        this._store.addChangedListener(this._onStoreChanged);
    }

    public componentWillUnmount(): void {
        this._actionCreator.stopMonitoringSchemeConversionProgress();
        this._store.removeChangedListener(this._onStoreChanged);
    }

    private _onStoreChanged = (): void => {
        this.setState(this._store.getState());
    }

    public render(): React.ReactNode {
        return (
            <div className="service-connection-overview-tab service-connections-tabs-container page-content page-content-top flex flex-grow flex-column">
                {this._getDetailsView()}
            </div>
        );
    }

    private _getConversionBanner(): JSX.Element {
        if (this._workloadIdentityFederationEnabled) {
            return <ServiceEndpointUpgradeBannerComponent />;
        } else {
            return null;
        }
    }

    private _getFinalizeDraftBanner(): JSX.Element {
        if (this._allowCreatingEndpointsInDraftStateEnabled) {
            return <ServiceEndpointFinalizeDraftBannerComponent onFinalizeClick={this.props.onFinalizeClick} />;
        } else {
            return null;
        }
    }

    private _getConversionDetails(): JSX.Element {
        if (this._workloadIdentityFederationEnabled) {
            return <ServiceEndpointUpgradeDetailsComponent />;
        }
        return null;
    }

    private _getFinalizeDraftDetails(): JSX.Element {
        if (this._allowCreatingEndpointsInDraftStateEnabled) {
            return <ServiceEndpointFinalizeDraftDetailsComponent />;
        } else {
            return null;
        }
    }

    private _getOperationStatusMessage(): JSX.Element {
        const endpoint = this.state.serviceEndpoint;
        if (!endpoint) {
            return;
        }

        const status = endpoint.operationStatus as AzureSpnOperationStatus;
        if (!status) {
            return;
        }

        const message = status.statusMessage;
        if (!message
            || StringUtil.equals(message, EndpointConstants.AzureEndpointConstants.ConvertAuthenticationSchemeFailedStatus, true)
            || StringUtil.equals(message, EndpointConstants.AzureEndpointConstants.ConvertAuthenticationSchemeInProgressStatus, true)) {
            return;
        }

        let severity = status.severity as MessageCardSeverity;
        if (!severity) {
            severity = MessageCardSeverity.Info;
        }

        return (<MessageCard className="margin-bottom-16" severity={severity}>
            {message}
        </MessageCard>);
    }

    private _getEndpointUrlsComponent(): JSX.Element {
        return (<ServiceEndpointUrlsComponent serviceEndpoint={this.state.serviceEndpoint} serviceEndpointType={this.state.serviceEndpointType} />);
    }

    private _getDetailsView(): JSX.Element {
        const authSchemeTypeText = this.state.serviceEndpoint && StringUtil.localeFormat(Resources.AuthSchemaNameText, this._getEndpointAuthScheme().toLocaleLowerCase()) || "";
        const endpointTypeText = this.state.serviceEndpointType && this.state.serviceEndpointType.displayName || "";
        const isDisabledText = this.state.serviceEndpoint && this.state.serviceEndpoint.isDisabled && Resources.DisabledTitleAppendix || "";

        return (<React.Fragment>

            {this._getOperationStatusMessage()}
            {this._getFinalizeDraftBanner()}
            {this._getConversionBanner()}

            <Card
                className="service-connection-details-card bolt-card-no-vertical-padding flex-column"
                contentProps={{ contentPadding: false }}
                titleProps={{
                    size: TitleSize.Medium,
                    text: Resources.DetailsCardTitle
                }}
            >
                <div className="flex-grow service-connection-details-container">
                    <div className="flex-row flex-wrap">
                        <div className="details-content-left-pane">
                            <div className="details-content-header body-s secondary-text font-weight-semibold">{Resources.ConnectionTypeTitle}</div>
                            <div className="flex-row flex-wrap">
                                <div className="service-connection-icon-container flex-column">
                                    <img className="service-connection-icon" src={this.state.serviceEndpointType && this.state.serviceEndpointType.iconUrl} alt="" role="presentation" />
                                </div>
                                <div className="flex-column service-connection-type-container">
                                    <span className="service-connection-type body-m">{endpointTypeText + isDisabledText}</span>
                                    <span className="service-connection-auth-scheme secondary-text body-s">{authSchemeTypeText}</span>
                                    {this._getEndpointUrlsComponent()}
                                </div>
                            </div>
                        </div>

                        <div className="details-content-right-pane">
                            <div className="details-content-header body-s secondary-text font-weight-semibold">{Resources.CreatorTitle}</div>
                            <div className="flex-row flex-wrap">
                                <div className="service-connection-creator-image-container flex-column">
                                    <VssPersona
                                        identityDetailsProvider={
                                            {
                                                getDisplayName: (): string => {
                                                    return this.state.serviceEndpoint && this.state.serviceEndpoint.createdBy.displayName;
                                                },
                                                getIdentityImageUrl: (size: number): string | undefined => {
                                                    return this.state.serviceEndpoint && this.state.serviceEndpoint.createdBy.imageUrl;
                                                }
                                            }
                                        }
                                        size={"medium"}
                                    />
                                </div>
                                <div className="flex-column service-connection-type-container">
                                    <span className="service-connection-creator-name body-m">{this.state.serviceEndpoint && this.state.serviceEndpoint.createdBy.displayName}</span>
                                    <span className="service-connection-creator-email secondary-text body-s">{this.state.serviceEndpointOwnerEmail}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    {
                        this.state.serviceEndpoint && this.state.serviceEndpoint.description &&
                        <div className="flex-row service-connection-description-container flex-wrap">
                            <div className="flex-column">
                                <div className="details-content-header body-s secondary-text font-weight-semibold">{Resources.DescriptionTitle}</div>
                                <div className="service-connection-description body-m">{this.state.serviceEndpoint.description}</div>
                            </div>
                        </div>
                    }
                    {
                        this.state.serviceEndpoint && this.state.eligibleSchemaDowngrade &&
                        <div className="padding-top-16">
                            <Link onClick={() => this.setState({ isRevertConfirmationDialogueOpen: true })}>
                                {Resources.RevertConversionToAnOriginalScheme}&nbsp;<Icon iconName="History" />
                            </Link>
                        </div>
                    }
                </div>
            </Card>

            {this._getConversionDetails()}
            {this._getFinalizeDraftDetails()}

            {this.state.isRevertConfirmationDialogueOpen && this._renderRevertConfirmationDialog()}
        </React.Fragment>
        );
    }

    private _renderRevertConfirmationDialog = () => {
        return <Dialog
            defaultActiveElement={".bolt-checkbox-label"}
            calloutContentClassName="service-endpoint-conversion-dialog"
            footerButtonProps={[
                {
                    onClick: () => {
                        this._closeRevertConfirmationDialog(false);
                    },
                    text: Resources.Cancel
                },
                {
                    primary: true,
                    onClick: () => {
                        this._closeRevertConfirmationDialog(true);
                    },
                    text: Resources.Revert
                }
            ]}
            lightDismiss={true}
            onDismiss={() => {
                this._closeRevertConfirmationDialog(false);
            }}
            titleProps={{ text: Resources.ConfirmConversionRevertDialogueTitle }}
        >
            <div className="margin-bottom-8">
                <span>{Resources.ConfirmConversionRevertDialogueMessage}</span>&nbsp;
                <Link target="_blank" href="https://aka.ms/azdo-rm-secret-deletion">{Resources.LearnMoreText}</Link>
            </div>
        </Dialog>;
    }

    private _closeRevertConfirmationDialog(decision: boolean) {
        this.setState({ isRevertConfirmationDialogueOpen: false });

        if (decision) {
            this._actionCreator.convertServiceEndpointAuthenticationScheme(
                this.state.serviceEndpoint,
                this.state.eligibleSchemaDowngrade
            );
        }
    }

    private _getUsageView(): JSX.Element {
        return (
            <Card
                className="service-connection-usage-card bolt-card-no-vertical-padding"
                contentProps={{ contentPadding: false }}
                titleProps={{
                    size: TitleSize.Medium,
                    text: Resources.UsageCardTitle
                }}
            >
                <div className="flex-grow service-connection-usage-container">
                    {this._getNoUsageView()}
                </div>
            </Card>
        );
    }

    private _getNoUsageView(): JSX.Element {
        return (
            <div className="no-usage-view justify-center">
                <Icon iconName={"Chart"} className={"no-usage-icon"} ariaLabel="" />
                <div className="message-container">
                    <span className="message-header">{Resources.NoResourceUsageHeader}</span>
                    <span> <Link href={this._learnMoreHref}>{Resources.LearnMoreText}</Link>{Resources.NoResourceUsageText}</span>
                </div>
            </div>
        );
    }

    private _getEndpointAuthScheme(): string {
        let authScheme = this.state.serviceEndpoint.authorization.scheme.toLowerCase();

        if (this.state.serviceEndpointType && this.state.serviceEndpointType.authenticationSchemes) {
            this.state.serviceEndpointType.authenticationSchemes.forEach((endpointAuthScheme: ServiceEndpointAuthenticationScheme) => {
                if (endpointAuthScheme.scheme.toLowerCase() === authScheme) {
                    authScheme = endpointAuthScheme.displayName;
                }
            });
        }

        return authScheme;
    }

    private _actionCreator: ServiceEndpointActionsCreator;
    private _store: ServiceEndpointStore;
    private _learnMoreHref = "https://aka.ms/SCUse";
}
